import type { DesignDefinition } from 'common/design-tokens/types';
import { UNIFORM_PROPS } from 'common/constants/component-props';
import {
	collectDesignPresetRequiredFontFaces,
	collectEffectiveFontFaces,
	hasPresetTypography,
	type PresetFontFace,
} from 'common/design-tokens/preset-fonts';
import { collectEffectiveStyleAssignments, type PresetDiagnostic } from 'common/design-tokens/preset-styles';
import { collectStoryStyleAssignments } from 'common/design-tokens/story-style-assignments';
import {
	canonicalFontFamily,
	createFontResolver,
	type ResolvedFont,
} from 'admin/components/common/AI/Chat/fonts/font-resolver';
import { registerResolvedFonts } from 'admin/components/pages/Story/StoryEditorChat/tools/update-project/register-resolved-fonts';
import type { IOrganizationFonts } from 'types/organization';
import type { StoryMediaQuery, StorySettingsType, StoryVersionType } from 'types/story';
import { validateDesignPresetFontFace } from './design-preset-binding';

const faceKey = ({ family, weight }: PresetFontFace) => `${canonicalFontFamily(family)}:${weight ?? ''}`;
const diagnosticKey = (diagnostic: PresetDiagnostic) =>
	`${diagnostic.code}:${diagnostic.property}:${diagnostic.platform}:${diagnostic.tokenId}`;
const isFontTokenError = (diagnostic: PresetDiagnostic) =>
	(diagnostic.property === 'fontFamily' || diagnostic.property === 'fontWeight') &&
	(diagnostic.code === 'missing-token' || diagnostic.code === 'incompatible-token');

function validateChangedFontDiagnostics(
	before: readonly PresetDiagnostic[],
	after: readonly PresetDiagnostic[],
	label: string
) {
	const previous = new Set(before.filter(isFontTokenError).map(diagnosticKey));
	const newError = after.find(diagnostic => isFontTokenError(diagnostic) && !previous.has(diagnosticKey(diagnostic)));
	if (newError) throw new Error(`${label} has an unresolved ${newError.property} token "${newError.tokenId}".`);
}

/** Validate changed typography first, then return one resource snapshot for the existing Save. */
export function prepareDesignPresetFonts({
	settings,
	design,
	mediaQuery,
	organizationFonts,
	data,
	includePresetDefinitions = true,
}: {
	settings: StorySettingsType;
	design: DesignDefinition;
	mediaQuery: StoryMediaQuery;
	organizationFonts?: IOrganizationFonts;
	data?: StoryVersionType['data'];
	includePresetDefinitions?: boolean;
}): StorySettingsType {
	const fontResolver = createFontResolver({ storyFonts: settings.fonts, organizationFonts, googleFonts: [] });
	const resolved = new Map<string, ResolvedFont>();
	const validateNewFaces = (before: PresetFontFace[], after: PresetFontFace[]) => {
		const previousFaces = new Set(before.map(faceKey));
		for (const required of after) {
			if (previousFaces.has(faceKey(required))) continue;
			const font = validateDesignPresetFontFace(
				fontResolver.resolveExact(required.family),
				required.family,
				required.weight
			);
			resolved.set(canonicalFontFamily(required.family), font);
		}
	};
	for (const preset of includePresetDefinitions ? design.presets ?? [] : []) {
		const before = settings.design;
		validateChangedFontDiagnostics(
			collectEffectiveStyleAssignments({ design: before, presetId: preset.id, mediaQuery, layers: [] })
				.diagnostics,
			collectEffectiveStyleAssignments({ design, presetId: preset.id, mediaQuery, layers: [] }).diagnostics,
			`Preset "${preset.name}"`
		);
		validateNewFaces(
			before ? collectDesignPresetRequiredFontFaces({ design: before, presetId: preset.id, mediaQuery }) : [],
			collectDesignPresetRequiredFontFaces({ design, presetId: preset.id, mediaQuery })
		);
	}
	if (data) {
		const previous = new Map(
			collectStoryStyleAssignments({ data, design: settings.design }).map(({ element, assignments }) => [
				element._id,
				assignments,
			])
		);
		for (const { element, assignments } of collectStoryStyleAssignments({ data, design })) {
			if (
				!element.uiConfig.componentProps[UNIFORM_PROPS.designPresetId] ||
				!hasPresetTypography(assignments) ||
				assignments.diagnostics.some(diagnostic => diagnostic.code === 'missing-preset')
			)
				continue;
			const before = previous.get(element._id);
			validateChangedFontDiagnostics(
				before?.diagnostics ?? [],
				assignments.diagnostics,
				`Block "${element._id}"`
			);
			validateNewFaces(
				before ? collectEffectiveFontFaces({ design: settings.design, mediaQuery, styles: before.styles }) : [],
				collectEffectiveFontFaces({ design, mediaQuery, styles: assignments.styles })
			);
		}
	}
	return {
		...settings,
		design,
		...(resolved.size ? { fonts: registerResolvedFonts(settings.fonts ?? {}, [...resolved.values()]) } : {}),
	};
}
